/**
 * Utility functions for dynamically loading the Bidgely Widgets SDK
 */

export interface WidgetsSdkConfig {
  pilotId: string; // e.g., '10057'
  subdomain?: string; // e.g., 'cx-editor-test1-dev' (extracted from frontend_url)
  environment: string; // e.g., 'dev', 'uat', 'productqa', 'prod'
  scenarioId?: string; // e.g., 'MS_1'
}

export interface LoadWidgetsSdkOptions {
  onLoad?: () => void;
  onError?: (error: Error) => void;
  timeout?: number; // milliseconds, default 10000
}

/**
 * Extract subdomain from frontend_url
 * Example: https://cx-editor-test1-dev.bidgely.com -> cx-editor-test1-dev
 */
export function extractSubdomainFromFrontendUrl(frontendUrl: string | undefined | null): string | null {
  if (!frontendUrl) return null;
  
  // Remove protocol and domain suffix
  let subdomain = frontendUrl.replace(/^https?:\/\//, '') // remove protocol
                            .replace(/\.bidgely\.com.*/, ''); // remove everything from .bidgely.com onwards

  // In case there are still path/query parts, remove anything after first slash
  const slashIndex = subdomain.indexOf('/');
  if (slashIndex !== -1) {
    subdomain = subdomain.substring(0, slashIndex);
  }

  return subdomain || null;
}

/**
 * Get the SDK script URL from config
 */
export function getSdkScriptUrl(config: WidgetsSdkConfig): string {
  const { environment, pilotId, subdomain } = config;
  return `https://widgets.bidgely.com/sdk/${environment}/GLOBAL/bundle.js?subdomain=${subdomain}`;
}


/**
 * Workaround for wc-sdk webpack publicPath bug (see JIRA ticket).
 *
 * The wc-sdk bundles are built with `output.publicPath: "./"`, so lazily
 * loaded chunks (e.g. bui.<hash>.chunk.js for INSIGHTS_USAGE) resolve
 * relative to the HOST page URL instead of the wc-sdk CDN. On our app this
 * produces requests like http://localhost:3100/content-management/static/js/bui...js,
 * which return index.html and crash with "SyntaxError: Unexpected token '<'".
 *
 * The SDK's webpack chunk-loading global (`webpackChunkmeraki`) invokes the
 * third element of a pushed chunk entry with the webpack runtime object,
 * which lets us overwrite `runtime.p` (publicPath) with the absolute wc-sdk
 * base URL. This works whether the entry is pushed before the runtime script
 * evaluates (queued entries are processed on startup) or after (push is
 * monkey-patched by the runtime). If the SDK scripts are removed and
 * reloaded, the new runtime re-processes the queued entry, so the patch
 * survives SDK reloads. Remove once wc-sdk ships publicPath "auto"/absolute.
 */
const WC_SDK_PUBLIC_PATH_PATCH_CHUNK_ID = 999999;

let wcSdkPublicPath: string | null = null;
let wcSdkRuntime: any = null;
let wcSdkPatchQueued = false;

function applyWcSdkPublicPath(): void {
  if (wcSdkRuntime && wcSdkPublicPath) {
    wcSdkRuntime.p = wcSdkPublicPath;
  }
}

export function installWcSdkPublicPathPatch(subdomain: string): void {
  wcSdkPublicPath = `https://${subdomain}.bidgely.com/wc-sdk/`;
  // Re-apply immediately if the runtime is already captured (e.g. subdomain
  // changed on pilot switch without an SDK reload).
  applyWcSdkPublicPath();

  if (wcSdkPatchQueued) return;
  wcSdkPatchQueued = true;

  const chunkGlobal = ((window as any).webpackChunkmeraki =
    (window as any).webpackChunkmeraki || []);
  chunkGlobal.push([
    [WC_SDK_PUBLIC_PATH_PATCH_CHUNK_ID],
    {},
    (runtime: any) => {
      wcSdkRuntime = runtime;
      applyWcSdkPublicPath();
      console.log('[widgets-sdk-loader] Patched wc-sdk webpack publicPath:', wcSdkPublicPath);
    },
  ]);
}

/**
 * Check if SDK script is already loaded in the DOM
 */
export function isSdkScriptLoaded(url: string): boolean {
  const scripts = document.querySelectorAll('script[src]');
  return Array.from(scripts).some(
    (script) => (script as HTMLScriptElement).src === url
  );
}

/**
 * Remove SDK script from DOM
 */
function removeSdkScript(url: string): void {
  const scripts = document.querySelectorAll('script[src]');
  scripts.forEach((script) => {
    if ((script as HTMLScriptElement).src === url) {
      script.remove();
    }
  });
}

/**
 * Load Widgets SDK script dynamically
 */
export function loadWidgetsSdk(
  config: WidgetsSdkConfig,
  options: LoadWidgetsSdkOptions = {}
): Promise<void> {
  const { onLoad, onError, timeout = 30000 } = options; // Default 30 seconds - SDK may take time to initialize

  return new Promise((resolve, reject) => {
    // Validate config
    if (!config.pilotId) {
      const error = new Error('pilotId is required');
      onError?.(error);
      reject(error);
      return;
    }

    const url = getSdkScriptUrl(config);

    // Fix lazy-chunk URLs before any SDK script evaluates (or re-fix on reload)
    if (config.subdomain) {
      installWcSdkPublicPathPatch(config.subdomain);
    }

    // Check if SDK is already loaded
    if (window.BidgelyWebSdk && isSdkScriptLoaded(url)) {
      console.log('[widgets-sdk-loader] SDK already loaded:', url);
      onLoad?.();
      resolve();
      return;
    }

    // Remove any existing SDK scripts with different URLs
    const existingScripts = document.querySelectorAll('script[src*="widgets.bidgely.com/sdk"]');
    existingScripts.forEach((script) => {
      const scriptUrl = (script as HTMLScriptElement).src;
      if (scriptUrl !== url) {
        console.log('[widgets-sdk-loader] Removing old SDK script:', scriptUrl);
        script.remove();
        // Clear global object if it exists
        if (window.BidgelyWebSdk) {
          delete (window as any).BidgelyWebSdk;
        }
      }
    });

    // If script with same URL already exists, check if SDK is available
    // If not available after a short wait, remove and reload the script (it may have failed)
    if (isSdkScriptLoaded(url)) {
      console.log('[widgets-sdk-loader] Script tag exists, checking if SDK is available...');
      
      // Quick check - if SDK is already available, resolve immediately
      if (window.BidgelyWebSdk) {
        console.log('[widgets-sdk-loader] SDK already available from existing script');
        onLoad?.();
        resolve();
        return;
      }
      
      // Wait a short time to see if SDK becomes available (script might still be loading)
      const quickCheckTimeout = setTimeout(() => {
        if (!window.BidgelyWebSdk) {
          console.warn('[widgets-sdk-loader] Script exists but SDK not available. Removing and reloading script...');
          // Remove the existing script - it may have failed to execute
          const existingScript = document.querySelector(`script[src="${url}"]`);
          if (existingScript) {
            existingScript.remove();
          }
          // Continue with script creation below
        } else {
          // SDK became available, resolve
          onLoad?.();
          resolve();
        }
      }, 2000); // Wait 2 seconds for SDK to appear
      
      // Also set up the normal waiting logic in case SDK appears
      let checkCount = 0;
      const checkInterval = setInterval(() => {
        checkCount++;
        if (window.BidgelyWebSdk) {
          clearInterval(checkInterval);
          clearTimeout(quickCheckTimeout);
          clearTimeout(timeoutId);
          console.log(`[widgets-sdk-loader] SDK became available after ${checkCount * 100}ms`);
          onLoad?.();
          resolve();
          return;
        }
        
        // Log progress every 5 seconds
        if (checkCount % 50 === 0) {
          console.log(`[widgets-sdk-loader] Still waiting for SDK... (${checkCount * 100}ms elapsed)`);
        }
      }, 100);

      const timeoutId = setTimeout(() => {
        clearInterval(checkInterval);
        clearTimeout(quickCheckTimeout);
        if (!window.BidgelyWebSdk) {
          const error = new Error(`SDK loading timeout after ${timeout}ms - window.BidgelyWebSdk not available`);
          console.error('[widgets-sdk-loader] Timeout waiting for SDK:', {
            url,
            timeout,
            checksPerformed: checkCount,
            scriptExists: isSdkScriptLoaded(url),
            windowHasSdk: !!window.BidgelyWebSdk
          });
          onError?.(error);
          reject(error);
        }
      }, timeout);

      return;
    }

    // Create and append script tag
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.crossOrigin = 'anonymous';

    let resolved = false;

    const cleanup = () => {
      script.onload = null;
      script.onerror = null;
    };

    // Add error handler directly on script element to catch load failures
    // This catches script loading errors (network, CORS, etc.)
    script.onerror = (event) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      const error = new Error(`Failed to load SDK script: ${url}. This may be due to CORS or network issues.`);
      console.error('[widgets-sdk-loader] Script load error (onerror):', error);
      // Don't let script errors break the page
      try {
        onError?.(error);
        reject(error);
      } catch (err) {
        console.error('[widgets-sdk-loader] Error in onError callback:', err);
        reject(error);
      }
    };

    script.onload = () => {
      if (resolved) return;
      cleanup();

      console.log('[widgets-sdk-loader] Bundle script loaded, waiting for deferred SDK scripts and window.BidgelyWebSdk...');
      
      // The bundle.js script dynamically creates deferred scripts (runtime, vendor, main)
      // We need to wait for those to load, then check for window.BidgelyWebSdk
      let checkCount = 0;
      let deferredScriptsLoaded = false;
      let deferredScriptsFound = false;
      
      // Listen for when deferred scripts are added to the DOM
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeName === 'SCRIPT') {
              const scriptNode = node as HTMLScriptElement;
              if (scriptNode.src && (
                scriptNode.src.includes('wc-sdk') || 
                scriptNode.src.includes('bidgely.com/wc-sdk') ||
                (scriptNode.src.includes('bidgely.com') && (scriptNode.src.includes('/static/js/') || scriptNode.src.includes('/js/')))
              )) {
                deferredScriptsFound = true;
                console.log('[widgets-sdk-loader] Detected deferred SDK script added:', scriptNode.src);
                
                // Listen for when this script loads
                scriptNode.addEventListener('load', () => {
                  console.log('[widgets-sdk-loader] Deferred SDK script loaded:', scriptNode.src);
                  // Check if SDK is now available
                  if (window.BidgelyWebSdk && !resolved) {
                    resolved = true;
                    clearInterval(checkInterval);
                    clearTimeout(timeoutId);
                    observer.disconnect();
                    console.log('[widgets-sdk-loader] SDK became available after deferred script load');
                    onLoad?.();
                    resolve();
                  }
                });
                
                scriptNode.addEventListener('error', (err) => {
                  console.error('[widgets-sdk-loader] Deferred SDK script failed to load:', scriptNode.src, err);
                });
              }
            }
          });
        });
      });
      
      // Start observing for script additions
      observer.observe(document.head, { childList: true, subtree: true });

      // Helper to check if deferred scripts are loaded
      const checkDeferredScripts = (): { loaded: boolean; count: number; details: string[] } => {
        // Check for scripts that bundle.js creates (runtime, vendor, main from wc-sdk)
        const allScripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];
        const sdkScripts = allScripts.filter(s => 
          s.src.includes('wc-sdk') || 
          s.src.includes('bidgely.com/wc-sdk') ||
          s.src.includes('bidgely.com') && (s.src.includes('/static/js/') || s.src.includes('/js/'))
        );
        
        const details: string[] = [];
        let loadedCount = 0;
        
        sdkScripts.forEach(s => {
          // readyState is a non-standard property but exists in browsers
          // Use type assertion to access it safely
          const script = s as HTMLScriptElement & { readyState?: string };
          const readyState = script.readyState;
          const isLoaded = readyState === 'complete' || readyState === 'loaded' || 
                          (!readyState && s.defer && document.readyState === 'complete');
          if (isLoaded) loadedCount++;
          details.push(`${s.src.split('/').pop()}: ${readyState || 'unknown'} (defer: ${s.defer})`);
        });
        
        return {
          loaded: sdkScripts.length > 0 && loadedCount === sdkScripts.length,
          count: sdkScripts.length,
          details
        };
      };

      // Wait for SDK to be available on window
      const checkInterval = setInterval(() => {
        checkCount++;
        
        // Check if deferred scripts are loaded (after a short delay to let bundle.js create them)
        if (checkCount > 10 && !deferredScriptsLoaded) {
          const deferredStatus = checkDeferredScripts();
          deferredScriptsLoaded = deferredStatus.loaded;
          if (deferredScriptsLoaded) {
            console.log('[widgets-sdk-loader] Deferred SDK scripts have loaded, checking for window.BidgelyWebSdk...', deferredStatus);
          }
        }
        
        // Check if SDK is available
        if (window.BidgelyWebSdk) {
          if (resolved) return;
          resolved = true;
          clearInterval(checkInterval);
          clearTimeout(timeoutId);
          observer.disconnect();
          console.log(`[widgets-sdk-loader] SDK loaded successfully after ${checkCount * 100}ms:`, url);
          onLoad?.();
          resolve();
          return;
        }
        
        // Log progress every 5 seconds with diagnostic info
        if (checkCount % 50 === 0) {
          const deferredStatus = checkDeferredScripts();
          console.log(`[widgets-sdk-loader] Waiting for SDK... (${checkCount * 100}ms elapsed)`, {
            windowHasBidgelyWebSdk: !!window.BidgelyWebSdk,
            bundleScriptLoaded: true,
            deferredScriptsLoaded: deferredStatus.loaded,
            deferredScriptsCount: deferredStatus.count,
            sdkScriptsInDOM: Array.from(document.querySelectorAll('script[src]') as NodeListOf<HTMLScriptElement>).filter((s) => 
              s.src.includes('wc-sdk') || s.src.includes('bidgely.com')
            ).map((s) => s.src),
            bidgelyGlobals: Object.keys(window).filter(k => k.toLowerCase().includes('bidgely'))
          });
        }
      }, 100);

      const timeoutId = setTimeout(() => {
        clearInterval(checkInterval);
        observer.disconnect();
        if (!resolved && !window.BidgelyWebSdk) {
          resolved = true;
          const deferredStatus = checkDeferredScripts();
          const error = new Error(`BidgelyWebSdk not available after script load (timeout: ${timeout}ms). Deferred scripts found: ${deferredScriptsFound}, loaded: ${deferredStatus.loaded} (${deferredStatus.count} scripts found). Check browser console for errors.`);
          console.error('[widgets-sdk-loader] Timeout waiting for SDK after script load:', {
            url,
            timeout,
            checksPerformed: checkCount,
            bundleScriptLoaded: true,
            deferredScriptsFound,
            deferredScriptsLoaded: deferredStatus.loaded,
            deferredScriptsCount: deferredStatus.count,
            deferredScriptsDetails: deferredStatus.details,
            windowHasSdk: !!window.BidgelyWebSdk,
            sdkScriptsInDOM: Array.from(document.querySelectorAll('script[src]') as NodeListOf<HTMLScriptElement>).filter((s) => 
              s.src.includes('wc-sdk') || s.src.includes('bidgely.com')
            ).map((s) => s.src),
            bidgelyRelatedKeys: Object.keys(window).filter(k => 
              k.toLowerCase().includes('bidgely') || 
              k.toLowerCase().includes('widget') ||
              k.toLowerCase().includes('sdk')
            )
          });
          onError?.(error);
          reject(error);
        }
      }, timeout);
    };

    script.onerror = (event) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      const error = new Error(`Failed to load SDK script: ${url}`);
      console.error('[widgets-sdk-loader] Script load error:', {
        url,
        event,
        error: event instanceof ErrorEvent ? event.error : event
      });
      onError?.(error);
      reject(error);
    };

    // Add error event listener to catch script execution errors
    const errorHandler = (event: ErrorEvent) => {
      try {
        // Only handle errors from our script
        if (event.filename === url || event.filename?.includes('widgets.bidgely.com') || event.filename?.includes('bidgely.com')) {
          // "Script error." is a CORS error - handle it gracefully
          if (event.message === 'Script error.' || event.message === '') {
            console.warn('[widgets-sdk-loader] Widget SDK script error (CORS) detected:', {
              filename: event.filename,
              message: 'Script error (CORS - error details not available)'
            });
            // Prevent error from propagating
            event.preventDefault();
            event.stopPropagation();
          } else {
            console.error('[widgets-sdk-loader] Script execution error:', {
              message: event.message,
              filename: event.filename,
              lineno: event.lineno,
              colno: event.colno,
              error: event.error
            });
          }
          // Don't reject here - let the timeout handle it, but prevent error propagation
          event.preventDefault();
          event.stopPropagation();
        }
      } catch (handlerError) {
        // If error handler throws, just log it
        console.error('[widgets-sdk-loader] Error in error handler:', handlerError);
      }
    };
    
    window.addEventListener('error', errorHandler);
    
    // Append to head
    document.head.appendChild(script);
    console.log('[widgets-sdk-loader] Loading SDK script:', url);
    
    // Also check if SDK becomes available before onload fires (for cached scripts)
    const preloadCheck = setInterval(() => {
      if (window.BidgelyWebSdk) {
        clearInterval(preloadCheck);
        window.removeEventListener('error', errorHandler);
        if (!resolved) {
          resolved = true;
          cleanup();
          console.log('[widgets-sdk-loader] SDK available before onload (cached script)');
          onLoad?.();
          resolve();
        }
      }
    }, 100);
    
    // Clear preload check and error handler after timeout
    setTimeout(() => {
      clearInterval(preloadCheck);
      window.removeEventListener('error', errorHandler);
    }, timeout);
  });
}

/**
 * Unload Widgets SDK (remove script and clear global object)
 */
export function unloadWidgetsSdk(): void {
  // Remove all SDK scripts
  const scripts = document.querySelectorAll('script[src*="widgets.bidgely.com/sdk"]');
  scripts.forEach((script) => {
    console.log('[widgets-sdk-loader] Removing SDK script:', (script as HTMLScriptElement).src);
    script.remove();
  });

  // Clear global object
  if (window.BidgelyWebSdk) {
    delete (window as any).BidgelyWebSdk;
    console.log('[widgets-sdk-loader] Cleared BidgelyWebSdk from window');
  }
}

